
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import SimpleConfirmationDialog from '@/components/shared/SimpleConfirmationDialog.vue';
import { collectErrors, fieldErrorMessages, generalErrorMessage } from '@/components/settings/identityIntegrations/integrationErrors';
import { protocolLabel } from '@/components/settings/identityIntegrations/protocolLabel';
import {
  IApiFieldError,
  identityIntegrationsService,
  IIdentityIntegration,
  IDENTITY_INTEGRATION_STATUS_ACTIVE,
} from '@/services/identityIntegrationsService';

const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
// `status` is not rendered next to any field: an Enable refusal (409, ref
// `status`) has to reach the general alert, or the operator sees nothing.
const MAPPED_FIELDS = ['name', 'slug'];

@Component({
  name: 'GeneralCard',
  components: { SimpleConfirmationDialog },
})
export default class GeneralCard extends Vue {
  @Prop({ required: true })
  protected integration!: IIdentityIntegration;
  @Prop({ default: false })
  protected canManage!: boolean;

  protected name: string = '';
  protected slug: string = '';
  protected busy: boolean = false;
  protected errors: IApiFieldError[] = [];
  protected disableDialog: boolean = false;

  created() {
    this.syncFromIntegration();
  }

  @Watch('integration')
  onIntegrationChanged() {
    this.syncFromIntegration();
  }

  get isActive(): boolean {
    return this.integration.status === IDENTITY_INTEGRATION_STATUS_ACTIVE;
  }

  get protocolLabel(): string {
    return protocolLabel(this, this.integration.protocol);
  }

  get dirty(): boolean {
    return this.name !== this.integration.name || this.slug !== this.integration.slug;
  }

  get valid(): boolean {
    return !!this.name.trim() && SLUG_PATTERN.test(this.slug);
  }

  get nameRules() {
    return [(v: string) => !!v || (this.$t('common.required') as string)];
  }

  get slugRules() {
    return [
      (v: string) => !!v || (this.$t('common.required') as string),
      (v: string) => !v || SLUG_PATTERN.test(v) || (this.$t('identityIntegrations.fields.slugInvalid') as string),
    ];
  }

  get generalError(): string | null {
    return generalErrorMessage(this, this.errors, MAPPED_FIELDS);
  }

  fieldError(field: string): string[] {
    return fieldErrorMessages(this, this.errors, field);
  }

  async submitSave() {
    await this.run(() =>
      identityIntegrationsService.update(this.integration.id, {
        name: this.name.trim(),
        slug: this.slug.trim(),
      })
    );
  }

  async submitEnable() {
    await this.run(() => identityIntegrationsService.enable(this.integration.id));
  }

  async submitDisable() {
    this.disableDialog = false;
    await this.run(() => identityIntegrationsService.disable(this.integration.id));
  }

  private async run(operation: () => Promise<IIdentityIntegration>) {
    this.busy = true;
    this.errors = [];
    try {
      const updated = await operation();
      this.$uiBus.emit('info', this.$t('identityIntegrations.saved') as string);
      this.$emit('updated', updated);
    } catch (error) {
      this.errors = collectErrors(error);
    } finally {
      this.busy = false;
    }
  }

  private syncFromIntegration() {
    this.name = this.integration.name;
    this.slug = this.integration.slug;
    this.errors = [];
  }
}
